import * as Sentry from '@sentry/browser';

/**
 * Determines if an error should be reported to Sentry on the frontend
 */
export function shouldReportToSentry(error: any): boolean {
  // Don't report network errors - these are often connectivity issues
  if (error?.message === 'Network Error') {
    return false;
  }

  // Don't report expected client errors (4xx status codes)
  if (error?.response?.status >= 400 && error?.response?.status < 500) {
    return false;
  }

  // Don't report auth failures - these are operational
  if (error?.response?.status === 401 || error?.response?.status === 403) {
    return false;
  }

  // Don't report common operational errors
  const operationalMessages = [
    'request timeout',
    'aborted',
    'cancelled',
    'not found',
    'validation failed',
    'unauthorized'
  ];

  const message = (error?.message || '').toLowerCase();
  if (operationalMessages.some(msg => message.includes(msg))) {
    return false;
  }

  // Report server errors and unexpected client errors
  return true;
}

/**
 * Report an error to Sentry with additional context if it's worth reporting
 */
export function reportError(error: any, context?: Record<string, any>): boolean {
  if (!shouldReportToSentry(error)) {
    return false;
  }

  // Add additional context
  if (context) {
    Sentry.withScope((scope) => {
      Object.entries(context).forEach(([key, value]) => {
        scope.setExtra(key, value);
      });

      // Add error classification
      scope.setTag('errorType', error.constructor.name);
      scope.setTag('hasResponse', !!error.response);
      
      if (error.response) {
        scope.setTag('statusCode', error.response.status.toString());
        scope.setTag('statusText', error.response.statusText);
        scope.setExtra('responseData', error.response.data);
      }

      if (error.config) {
        scope.setExtra('requestMethod', error.config.method);
        scope.setExtra('requestUrl', error.config.url);
        scope.setTag('requestTimeout', error.config.timeout?.toString() || 'default');
      }

      Sentry.captureException(error);
    });
  } else {
    Sentry.captureException(error);
  }

  return true;
}

/**
 * Report a critical application error that should always go to Sentry
 */
export function reportCriticalError(error: any, context?: Record<string, any>): void {
  Sentry.withScope((scope) => {
    scope.setLevel('fatal');
    scope.setTag('critical', 'true');

    if (context) {
      Object.entries(context).forEach(([key, value]) => {
        scope.setExtra(key, value);
      });
    }

    Sentry.captureException(error);
  });
}

/**
 * Add user context for errors
 */
export function setUserContext(user: {
  id?: string | number;
  username?: string;
  email?: string;
  [key: string]: any;
}): void {
  Sentry.setUser(user);
}

/**
 * Clear user context
 */
export function clearUserContext(): void {
  Sentry.setUser(null);
}

/**
 * Add breadcrumb for user actions
 */
export function addActionBreadcrumb(action: string, data?: Record<string, any>): void {
  Sentry.addBreadcrumb({
    message: `User action: ${action}`,
    category: 'user.action',
    level: 'info',
    data
  });
}

export default {
  shouldReportToSentry,
  reportError,
  reportCriticalError,
  setUserContext,
  clearUserContext,
  addActionBreadcrumb
};